/**
 * Glossary source-of-truth — moved out of pages/Glossary.jsx so both
 * the /glossary index and the new /glossary/:slug per-term pages read
 * from the same list. Every entry here becomes:
 *   - an anchor section on the /glossary index (browse experience)
 *   - a dedicated /glossary/:slug canonical page (GEO citation surface)
 *
 * Contract: each entry MUST have {term, body}. Optional: {also, category}.
 * Do NOT change the ordering below without regenerating sitemap.xml
 * because we treat term order as sitemap order.
 */

// Family-wide default `updatedAt` — used by the sitemap generator
// when a specific glossary entry has no explicit `updatedAt`. Bump
// this constant to signal Google the whole glossary was reviewed.
export const DEFAULT_GLOSSARY_UPDATED_AT = "2026-08-24T15:00:00+05:30";

export const GLOSSARY_ENTRIES = [
  // ---------- Traditional Indian styles ----------
  {
    term: "Jadau",
    also: "Jarau, Jadtar",
    category: "Traditional Indian style",
    body: "Jadau is the ancient Mughal-era Indian jewellery technique of embedding uncut gemstones and polki diamonds into a foundation of pure gold that has been heated until soft. The heated gold is then hammered around the stones to hold them in place — no prongs, no glue, no modern setting hardware. Jadau is closely associated with royal ateliers of Rajasthan and Gujarat, and remains the highest-value bridal jewellery in North India. Signature Jadau pieces include heavy Bali (earrings), Rani Haar (long necklaces) and Bajubandh (armbands)."
  },
  {
    term: "Polki",
    also: "Uncut diamond, Rose-cut diamond",
    category: "Traditional Indian style",
    body: "Polki refers to unfaceted, uncut natural diamonds — the raw stone before modern brilliant-cut faceting was invented. Polki stones are set in gold with a silver-leaf foil backing that reflects light back through the stone, giving Polki its distinctive soft glow (versus the sharp brilliance of faceted diamonds). Polki is the primary diamond variety used in Jadau jewellery and is prized in Indian bridal collections. Polki is not lab-grown; it is real diamond in its unfaceted form."
  },
  {
    term: "Kundan",
    also: "Kundan setting",
    category: "Traditional Indian style",
    body: "Kundan is a stone-setting technique — not a stone. It uses 24kt gold foil (kundan means 'refined gold') pushed around a gemstone to hold it in place, without prongs or a bezel. The foil is worked cold, using pressure alone, and the finished setting has a distinctive scalloped edge where the gold has been shaped by hand. Developed in the royal courts of Rajasthan, Kundan is the most common setting technique in Jadau jewellery and is signature to the Meenakari + Kundan reverse-work style."
  },
  {
    term: "Meenakari",
    also: "Meena, Enamel work",
    category: "Traditional Indian style",
    body: "Meenakari is the traditional Indian enamel work where powdered coloured glass is fused into recessed cavities in a gold base and fired at high temperature until it flows and solidifies as a glass-like inlay. The classical Meenakari palette is red, green, blue and white. Originated in Persia and brought to India by the Mughals, Meenakari was mastered in Jaipur where it remains a signature craft. In Jadau jewellery, Meenakari is often applied to the reverse side of the piece (called 'meena reverse'), so the wearer's skin sees colour while the front carries the stones."
  },
  {
    term: "Temple jewellery",
    also: "Nakshi, Temple work",
    category: "Traditional Indian style",
    body: "Temple jewellery refers to heavy, gold-focused pieces depicting Hindu deities (Lakshmi, Ganesh, Krishna) and temple motifs (kalasha, peacock, elephant, lotus). Originally commissioned by temples in Tamil Nadu and Andhra Pradesh to adorn deity idols, Temple jewellery is now the signature bridal style of South Indian weddings. Characterised by high-relief embossed figures, minimal stone work, and thick gold — often 22kt or higher."
  },
  {
    term: "Rajwadi",
    also: "Royal style, Rajputana",
    category: "Traditional Indian style",
    body: "Rajwadi is the umbrella term for the court-style, heavy jewellery of Rajasthan and Gujarat royal ateliers. Rajwadi pieces combine Jadau (uncut stone setting), Kundan (gold-foil setting) and Meenakari (enamel) into a single ornament, often across multiple layers. Signature Rajwadi pieces are the Aad (choker), Rakhdi (forehead ornament), Bajubandh (armband) and heavy Rani Haar (long chain)."
  },

  // ---------- Setting techniques ----------
  {
    term: "Chaton",
    also: "Chaton bezel",
    category: "Setting technique",
    body: "A chaton is the metal cup-shaped seat that holds a gemstone in place. In Indian jewellery, the chaton is often sculpted with decorative edges (scalloped, milgrain, embossed) so the seat itself is part of the design, not just a functional holder. Chaton settings are used across Kundan, Jadau, temple and modern styles."
  },
  {
    term: "Bezel",
    also: "Bezel setting",
    category: "Setting technique",
    body: "A bezel is a rim of metal that wraps around the perimeter of a gemstone to hold it in place. Unlike prongs, a bezel covers the edge of the stone. Bezel settings are common in modern minimal jewellery and in Polki work where the flat stone edge suits a wraparound rim."
  },
  {
    term: "Prong",
    also: "Claw setting",
    category: "Setting technique",
    body: "A prong (also called a claw) is a thin metal projection that grips a gemstone from above, holding it in a raised chaton or basket. Prong settings maximise light entering the stone and are the standard for solitaire and halo engagement rings. Common prong counts are 4, 6 (Tiffany-style) and 8 (heavier vintage settings)."
  },
  {
    term: "Cathedral setting",
    also: "Cathedral mount",
    category: "Setting technique",
    body: "A cathedral setting is a ring setting where the metal that holds the centre stone extends upward from the band in an arch, resembling a cathedral's flying buttresses. The setting adds height and drama to the stone and is popular in engagement rings."
  },
  {
    term: "Halo setting",
    also: "Halo ring",
    category: "Setting technique",
    body: "A halo setting surrounds the centre stone with a circle of smaller accent stones (usually diamonds or Polki), making the centre stone appear larger and adding sparkle across the whole piece. Halo settings are common in modern Indian engagement and cocktail rings."
  },
  {
    term: "Solitaire",
    category: "Setting technique",
    body: "A solitaire is a ring or pendant featuring a single centre stone with no accent stones. The setting can be a prong basket, bezel, or cathedral — the defining feature is that the design draws the eye entirely to the one stone."
  },

  // ---------- Craft techniques ----------
  {
    term: "Nakashi",
    also: "Nakshi, Nakash",
    category: "Craft technique",
    body: "Nakashi is the traditional Indian technique of hand-embossing decorative motifs into gold, typically working from the reverse side to create a raised relief on the front. Used extensively in temple jewellery and Rajwadi pieces. Nakashi work is what gives high-relief peacock, lotus and deity figures their dimensional depth."
  },
  {
    term: "Jaali",
    also: "Filigree, Lattice",
    category: "Craft technique",
    body: "Jaali (Hindi for 'net' or 'lattice') is the technique of piercing intricate geometric or floral cut-outs through gold sheet, creating a lace-like openwork pattern. Popular in Mughal-era architecture (Taj Mahal jaali screens) and adapted into jewellery as filigree work on chaandbalis, jhumkas and rani haars. Facetra's prompt engineering recognises jaali as a rendering keyword."
  },
  {
    term: "Ghungroo",
    category: "Craft technique",
    body: "Ghungroo are small metallic bells traditionally sewn onto anklets and dance ornaments to produce sound with movement. In modern jewellery, ghungroo are used as decorative dangles at the base of jhumkas and long earrings, and along the edge of Rani Haar necklaces."
  },
  {
    term: "Lost-wax casting",
    also: "Cire perdue, Investment casting",
    category: "Manufacturing",
    body: "Lost-wax casting is the manufacturing technique used for almost all fine jewellery. A wax model of the piece is embedded in a plaster investment mould, the wax is melted out (hence 'lost'), and molten metal is poured into the resulting cavity. When the plaster is broken away, the metal piece emerges. Facetra's CAD kit includes wall-thickness recommendations specifically for lost-wax casting (0.6–0.8mm minimum) so the CAD bench builds a printable master."
  },
  {
    term: "Rhodium plating",
    category: "Manufacturing",
    body: "Rhodium plating is a thin electroplated layer of rhodium (a platinum-group metal) applied to gold or silver jewellery to give it a bright white mirror finish and resistance to tarnish. White gold pieces are typically rhodium plated. Facetra's CAD-ready render mode strips rhodium plating from any render to show the bare-metal pre-polish casting reference."
  },

  // ---------- CAD & manufacturing ----------
  {
    term: "CAD",
    also: "Computer-Aided Design",
    category: "CAD workflow",
    body: "In jewellery, CAD refers to using 3D modelling software (Rhino, MatrixGold, JewelCAD, Firestorm) to design a piece as a manifold 3D solid that can be 3D-printed as a wax master and cast via lost-wax casting. Facetra generates the reference material (renders, multi-view sheets, BOM, 3D mesh) that a CAD operator uses to build the final manifold model — we do not replace CAD software."
  },
  {
    term: "BOM",
    also: "Bill of Materials",
    category: "CAD workflow",
    body: "A Bill of Materials is a structured breakdown of what a jewellery piece is made of: weight of yellow gold (in grams), stone inventory (count, size, weight, type), enamel colour applications, karigar hours estimate, and cost estimate at the current gold rate. Facetra auto-generates a BOM for every render using the live 22kt gold rate updated hourly."
  },
  {
    term: "Wall thickness",
    category: "CAD workflow",
    body: "Wall thickness is the minimum metal thickness required for a jewellery piece to survive casting, polishing and daily wear. Recommended minimums: DMLS metal printing 1–2mm; SLA resin master 0.5mm (0.4mm for detailed jaali); FDM 0.8–1.2mm; lost-wax cast master 0.6–0.8mm. Facetra's CAD kit README ships with this cheatsheet."
  },
  {
    term: "Retopology",
    category: "CAD workflow",
    body: "Retopology is the CAD operator's process of taking a raw 3D mesh (like the one Facetra generates) and rebuilding it as a clean, manifold, cast-ready CAD model in Rhino / MatrixGold. The AI mesh gives the operator geometry to trace; retopology turns it into a printable master."
  },
  {
    term: "Multi-view sheet",
    also: "Technical view sheet, Multiview",
    category: "CAD workflow",
    body: "A multi-view sheet is a set of consistent 0° / 45° / 90° / 180° renders of the same piece, showing the design from front, three-quarter, side and back. The CAD bench uses the multi-view sheet as reference geometry to model against. Facetra's multi-view generator locks silhouette across all four views so proportions never drift."
  },

  // ---------- Popular Indian piece names ----------
  {
    term: "Rani Haar",
    category: "Indian piece",
    body: "Rani Haar (queen's necklace) is a long, layered gold necklace often reaching the navel, traditionally worn by North Indian brides. Rani Haars combine multiple layers of Jadau work, Meenakari reverse, and Polki settings in a graduated cascade."
  },
  {
    term: "Bali",
    also: "Balis, Bala",
    category: "Indian piece",
    body: "Bali are large hoop earrings worn in Rajasthani and Punjabi bridal jewellery, typically executed in Jadau + Kundan work with Polki accents. They range from small daily-wear balis to heavy bridal balis 3–5 inches in diameter."
  },
  {
    term: "Jhumka",
    also: "Jhumkas, Jhumki",
    category: "Indian piece",
    body: "Jhumkas are bell-shaped hanging earrings characteristic of South Indian temple jewellery and North Indian traditional bridal wear. Modern jhumkas often combine gold Nakashi work with Meenakari colour accents and ghungroo dangles at the base."
  },
  {
    term: "Chandbali",
    also: "Chaand bali",
    category: "Indian piece",
    body: "Chandbali (moon earrings) are crescent-shaped earrings named for their half-moon shape. Mughal-era origin, now iconic across Indian bridal collections. Signature Chandbalis combine Polki, Kundan and Meenakari with jaali (lattice) cut-outs."
  },
  {
    term: "Choker",
    category: "Indian piece",
    body: "A choker is a short necklace that sits tight around the throat. In Indian bridal jewellery, chokers are executed in heavy Jadau + Meenakari work and worn together with a longer Rani Haar. The Rajwadi choker (called 'Aad') is a signature Rajasthani piece."
  },
  {
    term: "Bajubandh",
    also: "Bajuband, Vanki",
    category: "Indian piece",
    body: "A bajubandh is an armband worn on the upper arm, typical of Rajasthani and Gujarati bridal jewellery. Executed in Jadau + Kundan work with Meenakari reverse. The South Indian equivalent is called 'Vanki' and often takes an inverted-V shape."
  }
];

export function slugifyTerm(term) {
  return String(term || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getGlossaryTerm(slug) {
  const clean = String(slug || "").toLowerCase();
  return GLOSSARY_ENTRIES.find((e) => slugifyTerm(e.term) === clean) || null;
}

export function getRelatedTerms(slug, count = 4) {
  const current = getGlossaryTerm(slug);
  if (!current) return [];
  // Prefer same-category neighbours first, then fall back to others.
  const sameCat = GLOSSARY_ENTRIES.filter(
    (e) => e.category === current.category && slugifyTerm(e.term) !== slug,
  );
  const others = GLOSSARY_ENTRIES.filter(
    (e) => e.category !== current.category && slugifyTerm(e.term) !== slug,
  );
  return [...sameCat, ...others].slice(0, count);
}
